import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { api, IStatus, IChangeStatusRequest } from '../api/api';
import Loader from './Loader';
import { useToast } from './ToastContainer';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1001;
`;

const ModalContent = styled.div`
  background-color: #0E1F1F;
  border-radius: 16px;
  padding: 24px;
  width: 90%;
  max-width: 500px;
  border: 2px solid #284640;
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-height: 90vh;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    width: 95%;
    padding: 16px;
    border-radius: 12px;
    gap: 16px;
    margin: 16px;
  }
  
  @media (max-width: 480px) {
    width: calc(100% - 32px);
    padding: 16px;
    margin: 16px;
    max-height: calc(100vh - 32px);
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const ModalTitle = styled.h2`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 20px;
  color: #FFFFFF;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #838D90;
  font-size: 24px;
  cursor: pointer;
  padding: 4px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #172A2A;
    color: #FFFFFF;
  }
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: #FFFFFF;
`;

const Select = styled.select`
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid #284640;
  background-color: #172A2A;
  color: #FFFFFF;
  font-family: 'Ubuntu', sans-serif;
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #24BA7D;
  }
  
  option {
    background-color: #172A2A;
    color: #FFFFFF;
  }
  
  @media (max-width: 768px) {
    padding: 14px 16px;
    font-size: 16px;
  }
`;

const Option = styled.option`
  background-color: #172A2A;
  color: #FFFFFF;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 8px;
  
  @media (max-width: 768px) {
    flex-direction: column-reverse;
    gap: 8px;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  border-radius: 8px;
  border: none;
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 150px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 768px) {
    padding: 14px 24px;
    font-size: 16px;
    width: 100%;
    min-width: unset;
    height: 48px;
  }
  
  ${props => props.variant === 'primary' ? `
    background-color: #24BA7D;
    color: #FFFFFF;
    
    &:hover {
      background-color: #1E9A66;
    }
    
    &:disabled {
      background-color: #284640;
      color: #838D90;
      cursor: not-allowed;
    }
  ` : `
    background-color: transparent;
    color: #838D90;
    border: 1px solid #284640;
    
    &:hover {
      background-color: #172A2A;
      color: #FFFFFF;
    }
  `}
`;

interface ChangeStatusModalProps {
  isOpen: boolean;
  productCode: string;
  onClose: () => void;
  onSuccess: () => void;
}

const ChangeStatusModal: React.FC<ChangeStatusModalProps> = ({
  isOpen,
  productCode = '',
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState({
    statusId: ''
  });
  
  const [statuses, setStatuses] = useState<IStatus[]>([]);
  const [loading, setLoading] = useState(false);
  const { showSuccess, showError } = useToast();

  useEffect(() => {
    if (isOpen) {
      loadStatuses();
    }
  }, [isOpen]);

  const loadStatuses = async () => {
    setLoading(true);
    try {
      const data = await api.getStatuses();
      setStatuses(data);
    } catch (error) {
      console.error('Ошибка при загрузке статусов:', error);
      showError('Ошибка при загрузке статусов');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!productCode || !formData.statusId) {
      return;
    }

    setLoading(true);
    try {
      await api.setStatus({
        product_code: productCode,
        status_id: parseInt(formData.statusId)
      });
      
      showSuccess('Статус успешно изменен');
      handleClose();
      onSuccess();
    } catch (error) {
      console.error('Ошибка при изменении статуса:', error);
      showError('Ошибка при изменении статуса');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      statusId: ''
    });
    onClose();
  };

  const isFormValid = productCode && formData.statusId;

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Изменение статуса продукта</ModalTitle>
          <CloseButton onClick={handleClose}>
            ✕
          </CloseButton>
        </ModalHeader>

        <FormField>
          <Label>Статус</Label>
          <Select
            value={formData.statusId}
            onChange={(e) => setFormData(prev => ({ ...prev, statusId: e.target.value }))}
          >
            <Option value="">Выберите статус</Option>
            {statuses.map(status => (
              <Option key={status.id} value={status.id}>
                {status.name}
              </Option>
            ))}
          </Select>
        </FormField>

        <ButtonContainer>
          <Button variant="secondary" onClick={handleClose} disabled={loading}>
            Отмена
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={!isFormValid || loading}>
            {loading ? <Loader size="small" /> : 'Изменить статус'}
          </Button>
        </ButtonContainer>
      </ModalContent>
    </ModalOverlay>
  );
};

export default ChangeStatusModal;
